<template>
	<div class="dashboard-page">
		<!-- Hero greeting -->
		<div class="dashboard-hero">
			<div class="hero-left">
				<h1 class="hero-greeting">
					{{ greeting }},<span class="hero-comma">&nbsp;</span><span class="hero-name">{{ firstName }}</span>
				</h1>
				<p class="hero-subtitle">{{ templeLabel }}</p>
			</div>
			<div class="hero-center">
				<Transition name="logo-fade" mode="out-in">
					<img v-if="!showTempleLogo" :src="oriLogoFull" alt="ORI" class="hero-logo hero-logo--ori" key="ori" />
					<img v-else :src="templeLogo" alt="Templo" class="hero-logo hero-logo--temple" key="temple" />
				</Transition>
			</div>
			<div class="hero-right">
				<WisdomQuoteWidget />
			</div>
			<div class="hero-decoration">
				<div class="hero-orb hero-orb--1" />
				<div class="hero-orb hero-orb--2" />
				<div class="hero-orb hero-orb--3" />
			</div>
		</div>

		<v-container fluid class="pa-0 dashboard-content">
			<!-- Lembrete para ativar MFA (dispensável) -->
			<MfaReminderBanner />

			<!-- Cadastros necessários + sessão guiada no assistente (dispensável) -->
			<SetupChecklistCard />

			<!-- Loading skeleton -->
			<template v-if="loading">
				<v-row class="mb-4">
					<v-col v-for="i in 3" :key="i" cols="12" sm="4">
						<v-skeleton-loader type="card" height="110" class="skeleton-animated" />
					</v-col>
				</v-row>
				<v-row class="mb-4">
					<v-col v-for="i in 3" :key="i" cols="12" md="4">
						<v-skeleton-loader type="card" height="260" class="skeleton-animated" />
					</v-col>
				</v-row>
			</template>

			<!-- Dashboard content -->
			<template v-else>
				<!-- Row 1: KPI Cards -->
				<BigNumberCards :items="kpiItems" />

				<!-- Row 2: Widgets -->
				<v-row class="mb-4">
					<v-col cols="12" md="4" class="d-flex widget-col widget-col--1">
						<ActiveRitualCard class="flex-grow-1" />
					</v-col>
					<v-col cols="12" md="4" class="d-flex widget-col widget-col--2">
						<UpcomingRitualsWidget class="flex-grow-1" />
					</v-col>
					<v-col cols="12" md="4" class="d-flex widget-col widget-col--3">
						<BirthdayWidget class="flex-grow-1" />
					</v-col>
				</v-row>

				<!-- Row 3: Orixa distribution + Top consultants -->
				<v-row class="mb-4">
					<v-col cols="12" md="8" class="d-flex widget-col widget-col--4">
						<v-card class="chart-card flex-grow-1">
							<div class="chart-card__header">
								<v-icon size="18" class="chart-card__icon">mdi-chart-bar</v-icon>
								<span class="chart-card__title">
									{{ $t("dashboard.charts.orixaDistribution") }}
								</span>
							</div>
							<div class="chart-card__body">
								<BaseBarChart
									v-if="orixaLabels.length > 0"
									:labels="orixaLabels"
									:datasets="orixaDatasets"
									:height="220"
									hide-legend
								/>
								<EmptyState v-else icon="mdi-chart-bar" :text="$t('dashboard.charts.noData')" :icon-size="32" class="dashboard-chart-empty" />
							</div>
						</v-card>
					</v-col>
					<v-col cols="12" md="4" class="d-flex widget-col widget-col--5">
						<TopConsultantsWidget
							:consultants="data?.topConsultants ?? []"
							class="flex-grow-1"
						/>
					</v-col>
				</v-row>

				<!-- Row 4: Line charts -->
				<v-row>
					<v-col cols="12" md="4" class="widget-col widget-col--6">
						<v-card class="chart-card">
							<div class="chart-card__header">
								<v-icon size="18" class="chart-card__icon">mdi-account-group</v-icon>
								<span class="chart-card__title">
									{{ $t("dashboard.charts.weeklyConsultants") }}
								</span>
							</div>
							<div class="chart-card__body">
								<BaseBarChart
									v-if="weeklyLabels.length > 0"
									:labels="weeklyLabels"
									:datasets="weeklyDatasets"
									:height="260"
								/>
								<EmptyState v-else icon="mdi-chart-bar" :text="$t('dashboard.charts.noData')" :icon-size="32" class="dashboard-chart-empty" />
							</div>
						</v-card>
					</v-col>

					<v-col cols="12" md="4" class="widget-col widget-col--7">
						<v-card class="chart-card">
							<div class="chart-card__header">
								<v-icon size="18" class="chart-card__icon">mdi-account-heart</v-icon>
								<span class="chart-card__title">
									{{ $t("dashboard.charts.monthlyPersons") }}
								</span>
							</div>
							<div class="chart-card__body">
								<BaseLineChart
									v-if="personsLineLabels.length > 0"
									:labels="personsLineLabels"
									:datasets="personsLineDatasets"
									:height="260"
								/>
								<EmptyState v-else icon="mdi-chart-line" :text="$t('dashboard.charts.noData')" :icon-size="32" class="dashboard-chart-empty" />
							</div>
						</v-card>
					</v-col>

					<v-col cols="12" md="4" class="widget-col widget-col--8">
						<v-card class="chart-card">
							<div class="chart-card__header">
								<v-icon size="18" class="chart-card__icon">mdi-ticket</v-icon>
								<span class="chart-card__title">
									{{ $t("dashboard.charts.monthlyTickets") }}
								</span>
							</div>
							<div class="chart-card__body">
								<BaseLineChart
									v-if="ticketsLineLabels.length > 0"
									:labels="ticketsLineLabels"
									:datasets="ticketsLineDatasets"
									:height="260"
								/>
								<EmptyState v-else icon="mdi-chart-line" :text="$t('dashboard.charts.noData')" :icon-size="32" class="dashboard-chart-empty" />
							</div>
						</v-card>
					</v-col>
				</v-row>
			</template>
		</v-container>
	</div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useI18n } from "vue-i18n";
import { useAuthStore } from "@/stores/auth";
import oriLogoFull from "@/assets/images/logos/ori-logo-full.svg";
import WisdomQuoteWidget from "@/components/dashboard/WisdomQuoteWidget.vue";
import MfaReminderBanner from "@/components/dashboard/MfaReminderBanner.vue";
import SetupChecklistCard from "@/components/dashboard/SetupChecklistCard.vue";
import BigNumberCards from "@/components/common/BigNumberCards.vue";
import { BaseLineChart, BaseBarChart } from "@/components/common/charts";
import type { LineDataset, BarDataset } from "@/components/common/charts";
import ActiveRitualCard from "@/components/dashboard/ActiveRitualCard.vue";
import BirthdayWidget from "@/components/dashboard/BirthdayWidget.vue";
import UpcomingRitualsWidget from "@/components/dashboard/UpcomingRitualsWidget.vue";
import TopConsultantsWidget from "@/components/dashboard/TopConsultantsWidget.vue";
import EmptyState from "@/components/common/EmptyState.vue";
import { getDashboardData, type DashboardData } from "@/services/dashboardService";

const { t } = useI18n();
const authStore = useAuthStore();

const loading = ref(true);
const data = ref<DashboardData | null>(null);

// Hero logo toggle
const showTempleLogo = ref(false);
const templeLogo = computed(() => authStore.currentTemple?.logoUrl);
let logoInterval: ReturnType<typeof setInterval> | null = null;

// Hero
const firstName = computed(() => {
	const name = authStore.user?.displayName || "";
	return name.split(" ")[0] || name;
});

const templeLabel = computed(() => {
	return authStore.currentTemple?.name || "";
});

const greeting = computed(() => {
	const hour = new Date().getHours();
	if (hour < 12) return t("dashboard.greeting.morning");
	if (hour < 18) return t("dashboard.greeting.afternoon");
	return t("dashboard.greeting.evening");
});

// Row 1: KPI Cards
const kpiItems = computed(() => {
	if (!data.value) return [];
	return [
		{
			label: t("dashboard.kpi.consultants"),
			value: data.value.personStats.totalConsultants,
			icon: "mdi-account-heart",
			color: "primary",
		},
		{
			label: t("dashboard.kpi.mediums"),
			value: data.value.personStats.totalMediums,
			icon: "mdi-account-star",
			color: "deep-purple",
		},
		{
			label: t("dashboard.kpi.tickets"),
			value: data.value.ticketSummary.totalTickets,
			icon: "mdi-ticket",
			color: "amber-darken-1",
		},
	];
});

// Row 3: Orixa distribution
const orixaLabels = computed(() => {
	if (!data.value) return [];
	return data.value.orixaDistribution.map((o) => o.name);
});

const orixaDatasets = computed<BarDataset[]>(() => {
	if (!data.value || data.value.orixaDistribution.length === 0) return [];
	const distribution = data.value.orixaDistribution;
	return [
		{
			label: t("dashboard.charts.orixaDistribution"),
			color: distribution.map((o) => o.color ?? "#9E9E9E"),
			data: distribution.map((o) => o.count),
		},
	];
});

// Row 4: Bar — consulentes por semana
const weeklyLabels = computed(() => {
	if (!data.value) return [];
	return data.value.weeklyConsultants.map((w) => w.label);
});

const weeklyDatasets = computed<BarDataset[]>(() => {
	if (!data.value) return [];
	return [
		{
			label: t("dashboard.kpi.consultants"),
			color: "#66BB6A",
			data: data.value.weeklyConsultants.map((w) => w.total),
		},
	];
});

// Row 4: Line — consulentes por mes
const personsLineLabels = computed(() => {
	if (!data.value) return [];
	return data.value.monthlyPersons.map((m) => m.label);
});

const personsLineDatasets = computed<LineDataset[]>(() => {
	if (!data.value) return [];
	return [
		{
			label: t("dashboard.kpi.consultants"),
			color: "#42A5F5",
			data: data.value.monthlyPersons.map((m) => m.total),
			fill: true,
		},
	];
});

// Row 4: Line — fichas por mes
const ticketsLineLabels = computed(() => {
	if (!data.value) return [];
	return data.value.monthlyTickets.map((m) => m.label);
});

const ticketsLineDatasets = computed<LineDataset[]>(() => {
	if (!data.value) return [];
	return [
		{
			label: t("dashboard.kpi.tickets"),
			color: "#FFA726",
			data: data.value.monthlyTickets.map((m) => m.total),
			fill: true,
		},
	];
});

onMounted(async () => {
	try {
		data.value = await getDashboardData();
	} catch (err) {
		console.error("Failed to load dashboard data:", err);
	} finally {
		loading.value = false;
	}

	if (templeLogo.value) {
		logoInterval = setInterval(() => {
			showTempleLogo.value = !showTempleLogo.value;
		}, 10000);
	}
});

onUnmounted(() => {
	if (logoInterval) clearInterval(logoInterval);
});
</script>

<style lang="scss" scoped>
@use "@/styles/_variables.scss" as vars;
@use "@/styles/_theme-utilities.scss" as theme;

// ============================================
// PAGE
// ============================================
.dashboard-page {
	min-height: 100vh;
	padding-bottom: 2rem;
}

.dashboard-content {
	padding: 0 1.5rem;
}

// ============================================
// HERO GREETING
// ============================================
.dashboard-hero {
	position: relative;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 2rem;
	padding: 2.5rem 2rem 1.7rem;
	margin-bottom: 0.25rem;
	overflow: hidden;
}

.hero-left {
	position: relative;
	z-index: 1;
	flex-shrink: 0;
}

.hero-center {
	position: relative;
	z-index: 1;
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: center;
}

.hero-logo {
	opacity: 0.18;
	animation: fadeIn 1s ease-out 0.3s both;

	&--ori {
		height: 58px;
		width: auto;
	}

	&--temple {
		height: 72px;
		width: auto;
		max-width: 180px;
		object-fit: contain;
		opacity: 0.35;
	}
}

.logo-fade-enter-active,
.logo-fade-leave-active {
	transition: opacity 0.8s ease;
}

.logo-fade-enter-from,
.logo-fade-leave-to {
	opacity: 0;
}

.hero-right {
	position: relative;
	z-index: 1;
	flex: 1;
	max-width: 480px;
	min-width: 0;
}

.hero-greeting {
	font-family: vars.$font-heading;
	font-size: 1.75rem;
	font-weight: 700;
	color: rgb(var(--v-theme-on-background));
	margin: 0;
	line-height: 1.3;
	animation: fadeSlideIn 0.6s ease-out;
}

.hero-comma {
	color: rgb(var(--v-theme-primary));
}

.hero-name {
	color: rgb(var(--v-theme-primary));
}

.hero-subtitle {
	font-family: vars.$font-secondary;
	font-size: 0.95rem;
	color: rgb(var(--v-theme-on-background));
	opacity: 0.6;
	margin: 0.5rem 0 0;
	animation: fadeSlideIn 0.6s ease-out 0.1s both;
}

// Floating orbs — ambient decoration
.hero-decoration {
	position: absolute;
	top: 0;
	right: 0;
	bottom: 0;
	width: 300px;
	pointer-events: none;
	z-index: 0;
	overflow: hidden;
}

.hero-orb {
	position: absolute;
	border-radius: 50%;
	background: rgb(var(--v-theme-primary));
	opacity: 0.06;
	animation: float 8s ease-in-out infinite;

	&--1 {
		width: 120px;
		height: 120px;
		top: -20px;
		right: 40px;
		animation-delay: 0s;
	}

	&--2 {
		width: 80px;
		height: 80px;
		top: 30px;
		right: -10px;
		animation-delay: -3s;
		opacity: 0.04;
	}

	&--3 {
		width: 50px;
		height: 50px;
		bottom: -10px;
		right: 100px;
		animation-delay: -5s;
		opacity: 0.08;
	}
}

// ============================================
// CHART CARDS (themed)
// ============================================
.chart-card {
	border-radius: 12px !important;
	border: 1px solid rgba(var(--v-theme-primary), 0.08);
	background: rgb(var(--v-theme-surface));
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
	transition: all 0.3s ease;
	overflow: hidden;

	&:hover {
		box-shadow: 0 6px 20px rgba(var(--v-theme-primary), 0.08);
		transform: translateY(-2px);
	}

	&__header {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		padding: 0.875rem 1rem;
		border-bottom: 1px solid rgba(var(--v-theme-primary), 0.06);
	}

	&__icon {
		color: rgb(var(--v-theme-primary));
		opacity: 0.7;
	}

	&__title {
		font-family: vars.$font-secondary;
		font-size: 0.8125rem;
		font-weight: 600;
		color: rgb(var(--v-theme-on-surface));
		letter-spacing: 0.01em;
	}

	&__body {
		padding: 1rem;
	}
}

// ============================================
// EMPTY STATES (override for chart cards)
// ============================================
.dashboard-chart-empty {
	min-height: 220px;
}

// ============================================
// WIDGET STAGGER ANIMATION
// ============================================
.widget-col {
	animation: fadeSlideUp 0.5s ease-out both;

	@for $i from 1 through 8 {
		&--#{$i} {
			animation-delay: #{$i * 0.07}s;
		}
	}
}

// Skeleton shimmer
.skeleton-animated {
	animation: fadeSlideUp 0.3s ease-out both;
}

// ============================================
// RESPONSIVE
// ============================================
@media (max-width: 1280px) {
	.hero-center {
		display: none;
	}
}

@media (max-width: 960px) {
	.dashboard-hero {
		flex-direction: column;
		align-items: flex-start;
		gap: 1rem;
		padding: 1.5rem 1.5rem 1.25rem;
	}

	.hero-right {
		max-width: 100%;
		width: 100%;
	}
}

@media (max-width: 600px) {
	.dashboard-hero {
		padding: 1.25rem 1rem 1rem;
	}

	.hero-greeting {
		font-size: 1.35rem;
	}

	.hero-decoration {
		width: 200px;
	}

	.dashboard-content {
		padding: 0 1rem;
	}
}
</style>
